import { useEffect, useState } from "react";
import { Box, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { getFixedMarksPerBlankLabel } from "../../../utils/fillQuestionUtils";

// The value can't be re-derived from the parsed indices on every keystroke — that strips the comma
// the instant it's typed, so a second row number can never be added. Hold the raw string and resync
// only when the parsed indices diverge externally.
const RowIndicesField = ({ indices, onChange }) => {
  const canonical = (indices || []).map((i) => i + 1).join(", ");
  const [raw, setRaw] = useState(canonical);
  useEffect(() => {
    const parsed = raw
      .split(/[,\s]+/)
      .map((n) => parseInt(n, 10) - 1)
      .filter((n) => Number.isInteger(n) && n >= 0);
    if (parsed.map((i) => i + 1).join(", ") !== canonical) setRaw(canonical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonical]);
  return (
    <TextField
      size="small"
      label="Only these rows"
      value={raw}
      onChange={(e) => {
        setRaw(e.target.value);
        onChange(
          e.target.value
            .split(/[,\s]+/)
            .map((n) => parseInt(n, 10) - 1)
            .filter((n) => Number.isInteger(n) && n >= 0)
        );
      }}
      placeholder="e.g. 4, 5"
      helperText="Row numbers (1-based). Blank = every row."
      sx={{ width: 170 }}
    />
  );
};

const FillTypeMarksControls = ({
  marksMode,
  onMarksModeChange,
  fixedTotal,
  onFixedTotalChange,
  blankCount = 0,
  rowsInterchangeable = false,
  onRowsInterchangeableChange,
  showRowsInterchangeable = false,
  // Restrict "any order" to specific rows (0-based). Empty = every row swappable.
  interchangeableRowIndices = [],
  onInterchangeableRowIndicesChange,
  // How many printed lines make up one swappable row. A "Correct __ / Wrong __" pair printed as two
  // stacked one-box lines is otherwise read as two independent rows, and "any order" then lets the
  // Correct and Wrong values swap. 1/blank = one line per row.
  interchangeableRowSize = "",
  onInterchangeableRowSizeChange,
  // Every blank is one slot in a single pool, with no rows and no columns — a spinner's six sections,
  // where only HOW MANY of each value matters, not which box holds it. Blanks left empty in the mark
  // scheme accept any other value.
  blanksInterchangeable = false,
  onBlanksInterchangeableChange,
  semanticMatch = false,
  onSemanticMatchChange,
  showSemanticMatch = false,
  // "Written exactly like the mark scheme" — the blanks are compared literally, with no fuzzy tiers and
  // no AI. It contradicts semantic (AI) matching, so ticking it clears that one.
  exactMatch = false,
  onExactMatchChange,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
    <FormControlLabel
      control={
        <Checkbox
          checked={marksMode === "fixed"}
          onChange={(e) => onMarksModeChange(e.target.checked ? "fixed" : "sum")}
          size="small"
        />
      }
      label={
        <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
          Fixed total marks (fractional)
        </Typography>
      }
      sx={{ m: 0 }}
    />
    {marksMode === "fixed" && (
      <TextField
        size="small"
        label="Total marks"
        type="number"
        value={fixedTotal}
        onChange={(e) => onFixedTotalChange(e.target.value)}
        inputProps={{ min: 1 }}
        sx={{ width: 130 }}
        helperText={
          blankCount > 0
            ? `${getFixedMarksPerBlankLabel(fixedTotal, blankCount)} per blank`
            : "Split equally across blanks"
        }
      />
    )}
    {showRowsInterchangeable && onRowsInterchangeableChange && (
      <FormControlLabel
        control={
          <Checkbox
            checked={!!rowsInterchangeable}
            onChange={(e) => onRowsInterchangeableChange(e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
            Answers can be in any order (swap blanks/rows)
          </Typography>
        }
        sx={{ m: 0 }}
      />
    )}
    {showRowsInterchangeable && onBlanksInterchangeableChange && (
      <FormControlLabel
        control={
          <Checkbox
            checked={!!blanksInterchangeable}
            onChange={(e) => onBlanksInterchangeableChange(e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
            Any blank, any position (pool all blanks)
          </Typography>
        }
        sx={{ m: 0 }}
      />
    )}
    {showRowsInterchangeable && onRowsInterchangeableChange && rowsInterchangeable && onInterchangeableRowIndicesChange && (
      <RowIndicesField indices={interchangeableRowIndices} onChange={onInterchangeableRowIndicesChange} />
    )}
    {showRowsInterchangeable && onRowsInterchangeableChange && rowsInterchangeable && onInterchangeableRowSizeChange && (
      <TextField
        size="small"
        label="Lines per row"
        type="number"
        value={interchangeableRowSize ?? ""}
        onChange={(e) => onInterchangeableRowSizeChange(e.target.value)}
        inputProps={{ min: 2 }}
        placeholder="e.g. 2"
        helperText="Blank = 1. Use 2 for stacked pairs."
        sx={{ width: 150 }}
      />
    )}
    {showSemanticMatch && onSemanticMatchChange && (
      <FormControlLabel
        control={
          <Checkbox
            checked={!!semanticMatch}
            onChange={(e) => onSemanticMatchChange(e.target.checked)}
            size="small"
            disabled={!!exactMatch}
          />
        }
        label={
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
            Grade with AI (accept answers phrased differently)
          </Typography>
        }
        sx={{ m: 0 }}
      />
    )}
    {onExactMatchChange && (
      <FormControlLabel
        control={
          <Checkbox
            checked={!!exactMatch}
            onChange={(e) => {
              onExactMatchChange(e.target.checked);
              if (e.target.checked && onSemanticMatchChange) onSemanticMatchChange(false);
            }}
            size="small"
          />
        }
        label={
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#555" }}>
            Answer must be written exactly like this (no near-enough matching, no AI)
          </Typography>
        }
        sx={{ m: 0 }}
      />
    )}
  </Box>
);

export default FillTypeMarksControls;
